import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { KendoBaseComponent } from '../kendo-base/kendo-base.component';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { DocumentDescriptor, SpreadsheetComponent as KendoSpreadsheetComponent } from '@progress/kendo-angular-spreadsheet';

@Component({
    selector: 'tt-file-data-viewer',
    templateUrl: './file-data-viewer.component.html',
    styleUrls: ['./file-data-viewer.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class FileDataViewerComponent extends KendoBaseComponent implements OnChanges {
    @Input()
    public ttFilename: string = '';

    @Output()
    public ttFilenameChange = new EventEmitter<string>();

    @Input()
    public ttHeaderData: { value: string | number | boolean | Date | undefined; index: number }[] = [];

    @Output()
    public ttHeaderDataChange = new EventEmitter<{ value: string | number | boolean | Date | undefined; index: number }[]>();

    @Input()
    public ttData: { [key: string]: any }[] = [];

    @Output()
    public ttDataChange = new EventEmitter<{ [key: string]: any }[]>();

    /**
     * Whether to hide the toolbar for the spreadsheet. \
     * Native upload for the spreadsheet will become unavailable and another mean of opening a file must be provided.
     *
     * @default true
     */
    @Input()
    public set ttHideToolbar(value: BooleanInput) {
        this._hideToolbar = coerceBooleanProperty(value);
    }
    public get ttHideToolbar(): boolean {
        return this._hideToolbar;
    }
    private _hideToolbar = true;

    /**
     * Whether to hide the actionbar (where the formula for the cell is displayed) for the spreadsheet.
     *
     * @default true
     */
    @Input()
    public set ttHideActionbar(value: BooleanInput) {
        this._hideActionbar = coerceBooleanProperty(value);
    }
    public get ttHideActionbar(): boolean {
        return this._hideActionbar;
    }
    private _hideActionbar = true;

    /**
     * Whether to hide the sheetsbar for the spreadsheet. \
     * The user can't switch between sheets or create/remove sheets if it is hidden, thus there should be only one sheet if the sheetsbar is hidden.
     *
     * @default true
     */
    @Input()
    public set ttHideSheetsbar(value: BooleanInput) {
        this._hideSheetsbar = coerceBooleanProperty(value);
    }
    public get ttHideSheetsbar(): boolean {
        return this._hideSheetsbar;
    }
    private _hideSheetsbar = true;

    @ViewChild(KendoSpreadsheetComponent)
    public spreadsheet?: KendoSpreadsheetComponent;

    public hasSpreadsheet = false;

    private currentFile: File | null = null;

    public selectedEncoding: 'UTF-8' | 'Binary' | 'ISO-8859-1' = 'UTF-8';

    public encodings = ['UTF-8', 'Binary', 'ISO-8859-1'].map((encoding) => ({ item_id: encoding, item_name: encoding }));

    async onSpreadsheetChanged(event: any) {
        this.ttData = this.formatSpreadsheetData(this.spreadsheet!.spreadsheetWidget.toJSON());
        this.ttDataChange.emit(this.ttData);
    }

    async onFilesChanged(event: File[]) {
        const file = event[0];

        try {
            if (!!file) {
                this.currentFile = file;

                if (this.hasValidSpreadsheetExtension(file)) {
                    this.hasSpreadsheet = true;

                    await this.spreadsheet?.spreadsheetWidget.fromFile(file);
                    console.log(this.spreadsheet!.spreadsheetWidget.toJSON());
                    this.ttData = this.formatSpreadsheetData(this.spreadsheet!.spreadsheetWidget.toJSON());
                    this.ttDataChange.emit(this.ttData);
                    this.jsonData = '';
                } else {
                    this.readFileAsText(file);
                    this.hasSpreadsheet = false;
                }

                this.ttFilename = file.name;
                this.ttFilenameChange.emit(this.ttFilename);
            } else {
                this.currentFile = null;
                this.hasSpreadsheet = false;
                this.jsonData = '';
                this.ttFilename = '';
                this.ttFilenameChange.emit(this.ttFilename);
            }
        } catch (error) {
            this.hasSpreadsheet = false;
            this.ttFilename = '';
            this.ttFilenameChange.emit(this.ttFilename);
        } finally {
            // this.files = [];
        }
    }

    private hasValidSpreadsheetExtension(file: File) {
        return ['.xls', '.xlsv', '.xlsx', 'xlsm', 'xlsb', '.csv', '.gsheet'].some((extension) => file.name.endsWith(extension));
    }
    public jsonData = '';

    private readFileAsText(file: File) {
        if (this.selectedEncoding === 'Binary') {
            this.getFileReader().readAsArrayBuffer(file);
        } else {
            this.getFileReader().readAsText(file, this.selectedEncoding);
        }
    }

    public onEncodingChanged(_: 'UTF-8' | 'Binary' | 'ISO-8859-1') {
        if (!!this.currentFile) {
            this.readFileAsText(this.currentFile);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.['ttHideToolbar']) {
            this.ttHideToolbar = changes['ttHideToolbar'].currentValue;
        }

        if (changes?.['ttHideActionbar']) {
            this.ttHideActionbar = changes['ttHideActionbar'].currentValue;
        }

        if (changes?.['ttHideSheetsbar']) {
            this.ttHideSheetsbar = changes['ttHideSheetsbar'].currentValue;
        }
    }

    private formatSpreadsheetData(spreadsheetJSON: DocumentDescriptor) {
        const rows = [...(spreadsheetJSON?.sheets?.[0]?.rows ?? [])].filter((row) => row.cells?.some((cell) => cell.value !== undefined));
        const data: { [key: string]: any }[] = [];

        if (!!rows) {
            const columns = (rows.splice(0, 1)[0].cells?.filter((item) => item.value !== undefined && item.index !== undefined) ?? []) as { value: string | number | boolean | Date | undefined; index: number }[];

            this.ttHeaderData = columns;
            this.ttHeaderDataChange.emit(this.ttHeaderData);

            for (let row of rows) {
                const item: { [key: string]: any } = {};

                for (let { index, value } of columns) {
                    item['' + value!] = row.cells?.find((cell) => cell.index! == +index!)?.value ?? '';
                }

                data.push(item);
            }
        }

        return data;
    }

    private fileReader?: FileReader;

    private getFileReader(): FileReader {
        if (!this.fileReader) {
            this.fileReader = new FileReader();

            this.fileReader.onload = (_) => {
                let result = '';

                if (this.fileReader!.result instanceof ArrayBuffer) {
                    result = Array.from(new Uint8Array(this.fileReader!.result))
                        .map((byte) => String.fromCharCode(byte))
                        .join('');
                } else if (!!this.fileReader?.result) {
                    result = this.fileReader?.result;
                }

                if (!!result && typeof result === 'string') {
                    this.ttData = result.split('\n').map((value) => ({ value: value }));
                    this.ttDataChange.emit(this.ttData);

                    try {
                        const jsonData = JSON.stringify(this.ttData, null, 2);
                        this.jsonData = jsonData;
                    } catch (error) {
                        this.jsonData = result;
                    }
                }
            };
        }

        return this.fileReader!;
    }
}
